import { accentSelectedText } from "@ui/lib/accent-classes";
import { tv, type VariantProps } from "@ui/lib/tv";
import type { AccentColor } from "@ui/types/accent";

/**
 * Per-accent ring/outline literals for Tile. Tile anchors its rings on
 * {red: 400, green: 500, yellow: 500, purple: 600}; ToggleTile deliberately
 * sits one step lighter for green/purple (matching its illustrated icon
 * colours), so the ring matrices stay per-component while the selected-text
 * map is shared via `@ui/lib/accent-classes`.
 * Full literal strings so the Tailwind v4 scanner emits them.
 */
const TILE_ACCENTS: Record<
  AccentColor,
  {
    /** 2px inset ring for raised selected tiles. */
    raisedSelectedRing: string;
    /** 1px inset ring for flat hovered tiles. */
    flatHoverRing: string;
    /** 1px inset ring + 1px darker bottom edge for flat selected tiles. */
    flatSelectedRing: string;
    /** Focus-visible outline colour. */
    focusOutline: string;
    /** White gap + accent halo around the indicator disc (hover + selected). */
    indicatorRing: string;
    /** Indicator disc fill when selected. */
    indicatorSelectedBg: string;
  }
> = {
  red: {
    raisedSelectedRing: "shadow-[inset_0_0_0_2px_var(--color-red-400)]",
    flatHoverRing: "shadow-[inset_0_0_0_1px_var(--color-red-400)]",
    flatSelectedRing: "shadow-[inset_0_0_0_1px_var(--color-red-400),inset_0_-1px_0_0_var(--color-red-500)]",
    focusOutline: "outline-red-400",
    indicatorRing: "shadow-[0_0_0_2px_white,0_0_0_3px_var(--color-red-400)]",
    indicatorSelectedBg: "bg-red-400",
  },
  green: {
    raisedSelectedRing: "shadow-[inset_0_0_0_2px_var(--color-green-500)]",
    flatHoverRing: "shadow-[inset_0_0_0_1px_var(--color-green-500)]",
    flatSelectedRing: "shadow-[inset_0_0_0_1px_var(--color-green-500),inset_0_-1px_0_0_var(--color-green-600)]",
    focusOutline: "outline-green-500",
    indicatorRing: "shadow-[0_0_0_2px_white,0_0_0_3px_var(--color-green-500)]",
    indicatorSelectedBg: "bg-green-500",
  },
  yellow: {
    raisedSelectedRing: "shadow-[inset_0_0_0_2px_var(--color-yellow-500)]",
    flatHoverRing: "shadow-[inset_0_0_0_1px_var(--color-yellow-500)]",
    flatSelectedRing: "shadow-[inset_0_0_0_1px_var(--color-yellow-500),inset_0_-1px_0_0_var(--color-yellow-600)]",
    focusOutline: "outline-yellow-500",
    indicatorRing: "shadow-[0_0_0_2px_white,0_0_0_3px_var(--color-yellow-500)]",
    indicatorSelectedBg: "bg-yellow-500",
  },
  purple: {
    raisedSelectedRing: "shadow-[inset_0_0_0_2px_var(--color-purple-600)]",
    flatHoverRing: "shadow-[inset_0_0_0_1px_var(--color-purple-600)]",
    flatSelectedRing: "shadow-[inset_0_0_0_1px_var(--color-purple-600),inset_0_-1px_0_0_var(--color-purple-700)]",
    // Deliberately one step lighter than the purple rings.
    focusOutline: "outline-purple-500",
    indicatorRing: "shadow-[0_0_0_2px_white,0_0_0_3px_var(--color-purple-600)]",
    indicatorSelectedBg: "bg-purple-600",
  },
};

const TILE_ACCENT_COLORS = Object.keys(TILE_ACCENTS) as AccentColor[];

/** Tile itself takes no `color`, so its markup opens this scope to match. */
export const TILE_DEFAULT_COLOR = "green" as const satisfies AccentColor;

// ── Hierarchy × state × color, focus outline, and indicator matrices ────────
const accentCompoundVariants = TILE_ACCENT_COLORS.flatMap((color) => {
  const accent = TILE_ACCENTS[color];
  return [
    // Raised selected: colored inset border
    { hierarchy: "raised" as const, color, state: "selected" as const, class: { root: accent.raisedSelectedRing } },
    // Flat hover: colored inset border
    { hierarchy: "flat" as const, color, state: "hover" as const, class: { root: accent.flatHoverRing } },
    // Flat selected: colored bg + inset border
    {
      hierarchy: "flat" as const,
      color,
      state: "selected" as const,
      class: { root: `bg-primary ${accent.flatSelectedRing}`, label: accentSelectedText[color] },
    },
    // Focus visible
    { color, isFocusVisible: true, class: { root: accent.focusOutline } },
    // Hover indicator ring
    { color, state: "hover" as const, class: { indicator: accent.indicatorRing } },
    // Selected indicator (filled — same for radio and checkbox)
    {
      color,
      state: "selected" as const,
      class: { indicator: `${accent.indicatorSelectedBg} ${accent.indicatorRing}` },
    },
  ];
});

export const tile = tv({
  slots: {
    root: ["relative bg-white no-underline outline-none transition-shadow cursor-pointer", "block"],
    indicator: "inline-flex shrink-0 items-center justify-center rounded-full",
    label: "",
    description: "text-sm text-neutral-500",
  },
  variants: {
    hierarchy: {
      raised: {
        root: "shadow-md",
      },
      flat: {
        root: "shadow-[inset_0_-1px_0_0_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.12)]",
      },
      // Chrome-less: the tile carries no edge of its own, so a list of them
      // reads as navigation rather than as cards.
      plain: {
        root: "",
      },
    },
    shape: {
      pill: {
        root: "flex items-start rounded-5xl",
        label: "font-normal text-neutral-800",
      },
      rectangular: {
        root: "flex flex-col rounded-3xl p-5",
        label: "text-base font-semibold text-neutral-900",
        description: "mt-0.5 truncate",
      },
      // Icon beside label on one line — the compact nav row.
      row: {
        root: "flex items-center gap-3 rounded-2xl px-3 py-4",
        label: "font-medium text-base text-neutral-900",
        description: "truncate",
      },
    },
    color: {
      red: {},
      green: {},
      purple: {},
      yellow: {},
    },
    state: {
      default: {},
      hover: {},
      selected: {},
      // Navigation "you are here" (aria-current), distinct from accent
      // `selected` which marks a chosen value in wizard choice groups.
      current: { root: "shadow-[inset_0_0_0_2px_var(--color-neutral-800)]" },
      disabled: {
        root: "cursor-not-allowed bg-disabled_subtle shadow-[inset_0_0_0_1px_var(--color-border-disabled)]",
        indicator: "cursor-not-allowed bg-disabled_subtle border border-border-disabled",
      },
    },
    size: {
      xl: {},
      "3xl": {},
      "4xl": {},
      "5xl": {},
    },
    control: {
      radio: { indicator: "size-4" },
      checkbox: { indicator: "size-5" },
    },
    align: {
      vertical: {},
      horizontal: { root: "md:flex-1 items-center" },
    },
    isFocusVisible: {
      true: { root: "outline-2 outline-offset-2" },
    },
  },
  compoundVariants: [
    // Raised hover: elevated shadow
    {
      hierarchy: "raised",
      state: "hover",
      class: {
        root: "shadow-[0px_4px_14px_0px_rgba(0,0,0,0.12),0px_1px_0px_0px_rgba(0,0,0,0.08),0px_0px_0px_1px_rgba(0,0,0,0.05)]",
      },
    },

    // Default/hover indicator
    { state: "default", class: { indicator: "bg-white border border-neutral-300" } },
    { state: "hover", class: { indicator: "bg-white border border-neutral-300" } },

    // ── Row shape: a soft fill carries hover and "you are here", in place of
    // the ring the card shapes use (a ring on a chrome-less row reads as an
    // input, and at row height it crowds the label).
    { shape: "row", state: "hover", class: { root: "bg-neutral-25" } },
    { shape: "row", state: "current", class: { root: "bg-neutral-50 shadow-none" } },

    ...accentCompoundVariants,

    // ── Size × shape (pill sizing from wizard groups) ────────────────────

    {
      shape: "pill",
      size: "xl",
      class: { root: "min-h-12 px-5 py-3", indicator: "self-center", label: "ml-4 text-lg leading-6" },
    },
    {
      shape: "pill",
      size: "3xl",
      class: { root: "min-h-14 px-5 py-3.5", indicator: "self-center", label: "ml-4 text-xl leading-6" },
    },
    {
      shape: "pill",
      size: "4xl",
      class: { root: "min-h-[60px] px-6 py-4", indicator: "self-center", label: "ml-5 text-xl leading-6" },
    },
    {
      shape: "pill",
      size: "5xl",
      class: { root: "min-h-16 px-6 py-5", indicator: "self-center", label: "ml-5 text-xl leading-6" },
    },
  ],
  defaultVariants: {
    hierarchy: "raised",
    shape: "rectangular",
    color: TILE_DEFAULT_COLOR,
    state: "default",
    isFocusVisible: false,
  },
});

export type TileVariants = VariantProps<typeof tile>;

export const tileGroupLayout = {
  vertical: "flex w-full flex-col gap-3",
  horizontal: "flex w-full flex-col gap-3 md:flex-row md:gap-4",
} as const;

export type TileState = "default" | "hover" | "selected" | "current" | "disabled";

export interface TileRenderState {
  isDisabled: boolean;
  isFocusVisible: boolean;
  isHovered: boolean;
  isSelected: boolean;
}
